// Phase 8.7 — LiftedAtTheMoment Reports & Analytics
// Revenue/orders by day, top SKUs, section breakdown.

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { BarChart3, DollarSign, TrendingUp } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";

const WINDOWS = [
  { value: 7,   label: "7d" },
  { value: 30,  label: "30d" },
  { value: 90,  label: "90d" },
  { value: 365, label: "1y" },
];

function dollars(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function Sparkline({ data, max }) {
  if (!data?.length) return <div className="text-xs text-lifted-dim">No data</div>;
  const w = 600, h = 80;
  const xStep = data.length > 1 ? w / (data.length - 1) : w;
  const pts = data.map((d, i) => {
    const y = max === 0 ? h - 2 : h - 2 - ((d.revenue_cents / max) * (h - 8));
    return `${i * xStep},${y}`;
  }).join(" ");
  const areaPts = pts + ` ${w},${h} 0,${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none" data-testid="reports-sparkline">
      <polygon points={areaPts} fill="rgba(184,255,103,0.10)" />
      <polyline points={pts} fill="none" stroke="#b8ff67" strokeWidth={1.5} />
    </svg>
  );
}

export default function AdminStorefrontReports() {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/storefront/reports", { params: { window_days: windowDays } });
      setData(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    if (!data) return null;
    const revenue = data.daily_revenue.reduce((s, d) => s + d.revenue_cents, 0);
    const orders = data.daily_revenue.reduce((s, d) => s + d.orders, 0);
    return { revenue, orders, avg: orders ? Math.round(revenue / orders) : 0 };
  }, [data]);

  const maxDaily = useMemo(() =>
    Math.max(0, ...(data?.daily_revenue || []).map((d) => d.revenue_cents)),
    [data]);

  return (
    <AdminFeatureShell
      breadcrumb="LiftedAtTheMoment / Analytics"
      title="Reports & Analytics"
      description="Revenue, orders and SKU performance across the LiftedAtTheMoment storefront."
      status={totals ? `${totals.orders} orders in window` : null}
      testId="admin-reports"
    >
      <div className="flex flex-wrap gap-2" data-testid="reports-window-picker">
        {WINDOWS.map((w) => (
          <button key={w.value} onClick={() => setWindowDays(w.value)}
            className={`px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] ${
              windowDays === w.value ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40" : "border-white/10 text-lifted-dim hover:text-white"
            }`}
            data-testid={`reports-window-${w.value}`}>
            {w.label}
          </button>
        ))}
      </div>

      {err && <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm">{err}</div>}
      {!data && !err && <div className="text-sm text-lifted-dim">Loading…</div>}

      {data && totals && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="glass-panel p-4" data-testid="reports-kpi-revenue">
              <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase inline-flex items-center gap-1.5">
                <DollarSign className="h-3 w-3 text-lifted-green" /> Revenue
              </div>
              <div className="font-anton text-3xl text-white mt-1">{dollars(totals.revenue)}</div>
            </div>
            <div className="glass-panel p-4" data-testid="reports-kpi-orders">
              <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase inline-flex items-center gap-1.5">
                <BarChart3 className="h-3 w-3 text-lifted-green" /> Orders
              </div>
              <div className="font-anton text-3xl text-white mt-1">{totals.orders}</div>
            </div>
            <div className="glass-panel p-4" data-testid="reports-kpi-avg">
              <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase inline-flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3 text-lifted-green" /> Avg ticket
              </div>
              <div className="font-anton text-3xl text-white mt-1">{dollars(totals.avg)}</div>
            </div>
          </div>

          <div className="glass-panel p-5" data-testid="reports-daily">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">
              Revenue ({windowDays}d)
            </div>
            <Sparkline data={data.daily_revenue} max={maxDaily} />
            <div className="flex justify-between text-[0.6rem] text-lifted-dim mt-1 font-mono">
              <span>{data.daily_revenue[0]?.day || ""}</span>
              <span>{data.daily_revenue[data.daily_revenue.length - 1]?.day || ""}</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="glass-panel p-5" data-testid="reports-top-skus">
              <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">
                Top SKUs ({windowDays}d)
              </div>
              {data.top_skus.length === 0 ? (
                <div className="text-xs text-lifted-dim">No paid orders in this window.</div>
              ) : (
                <ul className="space-y-1.5 text-xs">
                  {data.top_skus.map((s) => (
                    <li key={s.handle} className="flex items-center justify-between gap-2">
                      <span className="truncate text-lifted-text">{s.title || s.handle}</span>
                      <span className="text-lifted-dim">{s.qty}× · {dollars(s.revenue_cents)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="glass-panel p-5" data-testid="reports-sections">
              <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-2">
                Section breakdown
              </div>
              <ul className="space-y-2 text-xs">
                {Object.entries(data.sections).map(([sec, s]) => (
                  <li key={sec} className="flex items-center justify-between" data-testid={`reports-section-${sec}`}>
                    <span className="uppercase tracking-[0.18em] font-bebas text-lifted-text">{sec}</span>
                    <span className="text-lifted-dim">{s.qty}× · {dollars(s.revenue_cents)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </AdminFeatureShell>
  );
}
